import type { PointerEvent } from 'react';
import React, { useCallback, useEffect } from 'react';
import useStage from '../../../providers/hooks/use-stage';
import type { StagePointerEventT } from '../../../providers/stage-provider';
import type { VertexT } from '../shape/vertex';
import { VERTEX_TYPE } from '../shape/vertex';
import { findPointExistOnTheLine } from '../utils';
export type EdgeRendererArgs<ElementT> = {
  points: string;
  onMouseEnter?: React.MouseEventHandler<ElementT>;
  onMouseLeave?: React.MouseEventHandler<ElementT>;
  onClick?: React.MouseEventHandler<ElementT>;
};
type PropsT<ElementT> = {
  isSelected: boolean;
  isDrawing?: boolean;
  points: Array<[number, number]>;
  ghostVertex: VertexT | null;
  edgeRenderer: (args: EdgeRendererArgs<ElementT>) => React.ReactNode;
  onGhostVertexChange: (newValue: VertexT | null | ((prevValue: VertexT | null) => VertexT | null)) => void;
  onAddNewPoints: (newPoints: [number, number][]) => void;
};
export function GhostPointRenderer<ElementT>({
  isSelected,
  points,
  isDrawing,
  ghostVertex,
  edgeRenderer,
  onAddNewPoints,
  onGhostVertexChange
}: PropsT<ElementT>) {
  const {
    stage
  } = useStage();
  const pointsStr = points.map(([x, y]) => `${x},${y}`).join(' ');
  const onClick = useCallback(() => {
    if (!ghostVertex) {
      return;
    }
    let selectedEdgeIndex: number | undefined;
    for (let i = 0; i < points.length; i++) {
      const start = points[i];
      // Doing modulo covers the last, a point between the end and start i.e points.length and 0
      const end = points[(i + 1) % points.length];
      // @ts-expect-error todo(ts-migration) TS2322 Type '[number, number] | undefined' is not assignable to type '[number, number]'.
      if (findPointExistOnTheLine(ghostVertex.point, {
        start,
        end
      }, 1)) {
        selectedEdgeIndex = i;
      }
    }
    if (selectedEdgeIndex === undefined) {
      return;
    }
    const newPoints = [...points.slice(0, selectedEdgeIndex + 1), ghostVertex.point, ...points.slice(selectedEdgeIndex + 1)];
    onAddNewPoints(newPoints);
  }, [ghostVertex, onAddNewPoints, points]);
  const onMouseHover = useCallback((e: PointerEvent<ElementT>) => {
    if (e.type === 'mouseenter') {
      // @ts-expect-error todo(ts-migration) TS2322 Type '[number, number] | undefined' is not assignable to type '[number, number]'.
      onGhostVertexChange({
        point: points[0],
        type: VERTEX_TYPE.MOVE
      });
    } else if (e.type === 'mouseleave') {
      onGhostVertexChange(null);
    }
  }, [onGhostVertexChange, points]);
  const onPointerMoveOnPolyline = useCallback((stageEvent: StagePointerEventT) => {
    onGhostVertexChange((prev: VertexT | null) => {
      if (!prev) {
        return prev;
      }
      return ({
        point: stageEvent.detail.mousePos,
        type: VERTEX_TYPE.MOVE
      } as VertexT);
    });
  }, [onGhostVertexChange]);
  useEffect(() => {
    if (!stage) return;
    if (isDrawing) return;
    stage.addEventListener('pointermove', onPointerMoveOnPolyline);
    return () => {
      stage.removeEventListener('pointermove', onPointerMoveOnPolyline);
    };
  }, [isDrawing, onPointerMoveOnPolyline, stage]);
  if (!isSelected) {
    return null;
  }
  return <>
      {edgeRenderer({
      points: pointsStr,
      onMouseEnter: onMouseHover,
      onMouseLeave: onMouseHover,
      onClick: onClick
    })}
    </>;
}